import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCrosshair, FiBox, FiLoader, FiArrowRight, FiCheckCircle, FiAlertCircle, FiX } = FiIcons;

const TrackingWidget = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [isTracking, setIsTracking] = useState(false);
  const [result, setResult] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  const handleTrack = (e) => {
    e.preventDefault();
    if (!trackingNumber) return;
    
    // Validate Tracking Format (e.g. NUS12345678)
    const regex = /^NUS\d{8}$/i;
    if (!regex.test(trackingNumber)) {
      setErrorModalOpen(true);
      return;
    }
    
    setIsTracking(true);
    setResult(null);

    // Simulate API Call
    setTimeout(() => {
      setIsTracking(false);
      setResult({
        status: 'In Transit',
        progress: 65,
        location: 'Hub Transit, Singapura',
        eta: 'Besok, 14:00 WIB',
        color: 'text-orange',
        bg: 'bg-orange'
      });
    }, 1500);
  };

  return (
    <div className="w-full max-w-4xl mx-auto relative z-50">
      {/* Floating Header - Dibuat w-full dan flex justify-center agar 100% rata tengah */}
      <div className="mb-6 w-full flex justify-center">
        <h3 className="text-white text-xl md:text-2xl font-bold flex items-center gap-3 drop-shadow-lg text-center">
          <SafeIcon icon={FiCrosshair} className="text-orange drop-shadow-[0_0_8px_rgba(30,144,255,0.8)]" />
          Pelacakan Real-Time
        </h3>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-3xl p-6 md:p-8 shadow-[0_20px_50px_-12px_rgba(10,31,75,0.2)] border border-slate-100">
        <form onSubmit={handleTrack} className="flex flex-col md:flex-row gap-4 relative">
          <div className="relative flex-1">
            <SafeIcon icon={FiBox} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 text-xl" />
            <input 
              type="text" 
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="Masukkan Nomor Resi / AWB (Contoh: NUS12345678)" 
              className="w-full bg-slate-50 border border-slate-200 rounded-2xl py-4 md:py-5 pl-14 pr-4 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-orange focus:ring-4 focus:ring-orange/10 transition-all font-mono text-base md:text-lg"
            />
          </div>
          <button 
            type="submit" 
            disabled={isTracking}
            className="bg-[#e46135] hover:bg-[#c54a22] text-white px-8 py-4 md:py-5 rounded-2xl font-bold text-base md:text-lg transition-all hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-3 min-w-[200px]"
          >
            {isTracking ? (
              <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
                <SafeIcon icon={FiLoader} className="text-2xl" />
              </motion.div>
            ) : (
              <>Lacak Kargo <SafeIcon icon={FiArrowRight} className="text-xl" /></>
            )}
          </button>
        </form>

        {/* Result UI */}
        <AnimatePresence>
          {result && (
            <motion.div 
              initial={{ opacity: 0, height: 0, marginTop: 0 }} 
              animate={{ opacity: 1, height: 'auto', marginTop: 32 }} 
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              className="border-t border-slate-100 overflow-hidden"
            >
              <div className="pt-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                  <div className="flex items-center gap-4">
                     <div className="w-12 h-12 rounded-full bg-orange/10 flex items-center justify-center">
                        <SafeIcon icon={FiCheckCircle} className="text-orange text-2xl" />
                     </div>
                     <div>
                      <p className="text-slate-500 text-sm mb-1 font-medium">Status Pengiriman</p>
                      <h4 className={`text-2xl font-bold ${result.color} tracking-tight`}>{result.status}</h4>
                     </div>
                  </div>
                  <div className="md:text-right bg-slate-50 px-6 py-3 rounded-xl border border-slate-100">
                    <p className="text-slate-500 text-xs mb-1 font-semibold uppercase tracking-wider">Estimasi Tiba (ETA)</p>
                    <h4 className="text-navy font-bold text-lg">{result.eta}</h4>
                  </div>
                </div>

                {/* Progress Bar with Shimmer Animation */}
                <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden mb-4 shadow-inner">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${result.progress}%` }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                    className={`absolute top-0 left-0 h-full ${result.bg} rounded-full relative overflow-hidden`}
                  >
                    <motion.div
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                      className="absolute top-0 bottom-0 w-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-[-20deg]"
                    />
                  </motion.div>
                </div>
                
                <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
                  <span className="text-orange">Diproses</span>
                  <span className="text-orange">Berangkat</span>
                  <span className="text-navy">Transit ({result.location})</span>
                  <span className="text-slate-300">Tiba</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Error Modal Format Resi */}
      <AnimatePresence>
        {errorModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-navy/80 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-3xl p-6 md:p-8 w-full max-w-sm shadow-2xl relative text-center"
            >
              <button 
                onClick={() => setErrorModalOpen(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-navy transition-colors"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
              
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiAlertCircle} className="text-red-500 text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-2">Format Resi Salah</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Mohon masukkan nomor resi dengan format <strong>NUS</strong> diikuti 8 angka. <br/><span className="text-sm border-t border-slate-100 pt-2 float-left w-full mt-2 inline-block">(Contoh: NUS12345678)</span>
              </p>
              <button 
                onClick={() => setErrorModalOpen(false)}
                className="w-full bg-[#e46135] hover:bg-[#c54a22] text-white font-bold py-3.5 rounded-xl transition-colors shadow-lg shadow-orange/20"
              >
                Coba Lagi
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TrackingWidget;